/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ChevronDown, FileSpreadsheet } from 'lucide-react';
import { UnstyledButton } from '@/shared/ui';
import { hasPermission } from '@/shared/utils/permissions';
import { useAgencyPermissions } from '@/features/agency/hooks/use-agency-permissions';
import { useSpreadsheetExport } from '../hooks/useSpreadsheetExport';
import type { SpreadsheetEntity } from '../types';
import { SpreadsheetMenuPanel } from './SpreadsheetMenuPanel';

export interface SpreadsheetMenuProps {
  /** Which listing the menu was opened from. */
  entity: SpreadsheetEntity;
  /** The agency the listing belongs to. */
  agencyId: string;
  /** How many records the current filters match, for the export help line. */
  totalCount: number;
  /** The active filters, forwarded verbatim to the export. */
  filters: Record<string, unknown>;
  /** The visible column keys, so the export matches what is on screen. */
  columns: string[];
}

/**
 * Trigger style.
 */
const triggerStyle = (open: boolean): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  padding: 'var(--space-2) var(--space-3)',
  fontSize: 'var(--text-body-sm)',
  fontWeight: 'var(--font-medium)' as unknown as number,
  background: open ? 'var(--surface-hover)' : 'transparent',
  color: 'var(--text-secondary)',
  border: '1px solid var(--border-medium)',
  borderRadius: 'var(--radius-md)',
  cursor: 'pointer',
  fontFamily: 'var(--font-family)',
  whiteSpace: 'nowrap',
});

/**
 * The "Planilha" button and its menu, sitting to the left of a listing's
 * primary CTA.
 *
 * Built on a plain button plus an absolutely-positioned panel rather than a
 * Radix Popover: this repo has no `@radix-ui/react-popover` dependency, and
 * `DatePickerPopover` already established the click-outside/Escape pattern
 * used here.
 *
 * @param props - Entity, agency, and the current listing query.
 * @returns The trigger and, while open, the menu panel.
 */
export const SpreadsheetMenu: React.FC<SpreadsheetMenuProps> = ({
  entity, agencyId, totalCount, filters, columns,
}) => {
  const { t } = useTranslation('features/spreadsheets');
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { exportAs } = useSpreadsheetExport(agencyId, entity, filters, columns);
  const { currentUserRole } = useAgencyPermissions();
  const canImport = hasPermission('import_spreadsheet', currentUserRole);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <UnstyledButton
        type="button"
        data-testid="spreadsheet-menu-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
        style={triggerStyle(open)}
      >
        <FileSpreadsheet width={14} height={14} />
        {t('menu.trigger')}
        <ChevronDown width={12} height={12} />
      </UnstyledButton>
      {open && (
        <SpreadsheetMenuPanel
          entity={entity}
          totalCount={totalCount}
          onImport={() => { setOpen(false); navigate(`../${entity}/import`); }}
          onExport={(format) => { setOpen(false); exportAs(format); }}
          onOpenHub={() => { setOpen(false); navigate('../spreadsheets'); }}
          canImport={canImport}
        />
      )}
    </div>
  );
};
